import { createContext, useContext } from 'react';

/**
 * Context-based trigger for the global WorldSelectionSheet (Batch 10
 * Sub-phase D — Decision #13 Path A).
 *
 * State stays in `AppShell` (preserves the existing `renderChrome`
 * unmount-on-/now-playing behavior); this Context is purely a
 * callback transport — descendants invoke `openSheet()` to flip the
 * AppShell-owned state. The hook deliberately does NOT expose
 * `setOpen` or `open` — descendants can't accidentally close the
 * sheet or read its state, which keeps the API minimal and the
 * sheet's lifecycle owned in one place.
 *
 * Misuse is caught loudly: `useWorldSelectionSheet()` throws when
 * called outside a `WorldSelectionSheetProvider`. Tests that render
 * components which use the hook must wrap renders in the provider
 * with a `vi.fn()` callback for assertions.
 *
 * Hook + context live in this `.ts` file (separate from the Provider
 * `.tsx`) so the Provider file stays component-only and satisfies
 * react-refresh/only-export-components.
 */

export interface WorldSelectionSheetContextValue {
  openSheet: () => void;
}

export const WorldSelectionSheetContext = createContext<WorldSelectionSheetContextValue | null>(
  null,
);

export function useWorldSelectionSheet(): WorldSelectionSheetContextValue {
  const ctx = useContext(WorldSelectionSheetContext);
  if (ctx === null) {
    throw new Error(
      'useWorldSelectionSheet must be used within a WorldSelectionSheetProvider. ' +
        'In production code, this is provided by AppShell. In tests, wrap your render() ' +
        'with <WorldSelectionSheetProvider openSheet={vi.fn()}>.',
    );
  }
  return ctx;
}
